import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { formatUtcDate } from "../../shared/date-utils";
import { ActivatedRoute, Router } from "@angular/router";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { ModernizationReadinessService } from "../../services/modernization-readiness.service";
import { AiSettingsService } from "../../services/ai-settings.service";
import { 
  AiConnection,
  AiModernizationReadinessSettings,
  AiModernizationReadinessSettingsDetail,
  AiModernizationReadinessSettingsVersionSummary,
  ModernizationReadinessJob,
  ModernizationReadinessJobStatus,
  ModernizationReadinessCalculationStatus,
  LiveLogEntry,
  LiveLogResponse
} from "../../models/chat.models";
import { forkJoin, interval, of, Subscription } from "rxjs";
import { catchError, switchMap, startWith } from "rxjs/operators";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MaterialModule } from "../../material.module";
import { SpinnerComponent } from "../../shared/spinner/spinner.component";
import { ModernizationReadinessSkeletonComponent } from "./modernization-readiness-skeleton/modernization-readiness-skeleton.component";
import { ModernizationReadinessJobHistorySkeletonComponent } from "./modernization-readiness-job-history-skeleton/modernization-readiness-job-history-skeleton.component";

@Component({
  selector: "modernization-readiness-calculator",
  templateUrl: "./modernization-readiness.component.html",
  styleUrls: ['./modernization-readiness.component.scss'],
  imports: [CommonModule, FormsModule, MaterialModule, SpinnerComponent, ModernizationReadinessSkeletonComponent, ModernizationReadinessJobHistorySkeletonComponent]
})
export class ModernizationReadinessComponent implements OnInit, OnDestroy {
  // Tab state
  selectedTabIndex = 0;
  
  // Connections (for dropdown)
  connections: AiConnection[] = [];
  
  // Settings
  activeSettings: AiModernizationReadinessSettingsDetail | null = null;
  editingSettings: AiModernizationReadinessSettings | null = null;
  settingsVersions: AiModernizationReadinessSettingsVersionSummary[] = [];
  showVersionHistory = false;
  isCreatingSettings = false;
  
  // Calculation Status
  calculationStatus: ModernizationReadinessCalculationStatus | null = null;
  currentJob: ModernizationReadinessJob | null = null;

  // Live log console
  logLines: string[] = [];
  lastLogId = 0;
  private logSubscription?: Subscription;
  
  // Job History
  jobHistory: ModernizationReadinessJob[] = [];
  jobHistoryPage = 1;
  jobHistoryPageSize = 20;
  jobHistoryTotalCount = 0;
  jobHistoryTotalPages = 0;
  jobHistoryLoading = false;
  
  // UI state
  loading = false;
  saving = false;
  triggering = false;
  singleAppTriggering = false;
  waitingForJob = false; // True while waiting for polling to pick up newly created job
  pausing = false;
  resuming = false;
  cancelling = false;
  error: string | null = null;
  successMessage: string | null = null;
  singleAppQuery: string | null = null;
  lastCompletedJobId: number | null = null; // Track if we should keep showing completed job
  private waitingForJobTimer?: any;
  
  // Status polling
  private statusSubscription?: Subscription;
  
  // Enum for template
  ModernizationReadinessJobStatus = ModernizationReadinessJobStatus;
  
  // typeof helper for template
  typeof = (val: any) => typeof val;

  @ViewChild('jobHistoryPaginator') jobHistoryPaginator?: MatPaginator;
  @ViewChild('logConsoleEl') logConsoleEl?: ElementRef;

  // Display columns
  jobHistoryColumns: string[] = ['id', 'createdAt', 'status', 'progress', 'duration', 'results', 'actions'];

  constructor(
    private modernizationReadinessService: ModernizationReadinessService,
    private aiSettingsService: AiSettingsService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['tab'] !== undefined) {
        this.selectedTabIndex = +params['tab'];
      }
    });
    this.loadAllData();
    
    // Poll calculation status every 3 seconds
    this.statusSubscription = interval(3000)
      .pipe(
        startWith(0),
        switchMap(() => this.modernizationReadinessService.getCalculationStatus())
      )
      .subscribe({
          next: (status) => {
            this.calculationStatus = status;
            
            // If we were waiting for job and polling now has a job, clear the waiting flag
            if (this.waitingForJob && (status.currentJob || status.isRunning || status.isPaused)) {
              this.waitingForJob = false;
              if (this.waitingForJobTimer) {
                clearTimeout(this.waitingForJobTimer);
                this.waitingForJobTimer = undefined;
              }
            }
            
            // Update current job from status
            if (status.currentJob) {
              const previousStatus = this.currentJob?.status;
              const wasRunning = previousStatus && this.isStatusRunning(previousStatus);
              this.currentJob = status.currentJob;
              // Track if job just completed and fetch full details including tokens
              if (this.currentJob && 
                  (this.isStatusCompleted(this.currentJob.status) ||
                   this.isStatusFailed(this.currentJob.status) ||
                   this.isStatusCancelled(this.currentJob.status))) {
                           
                // If job just transitioned to completed state, OR if tokens are missing, fetch full details
                if ((wasRunning || this.currentJob.totalTokens === 0 || !this.currentJob.totalTokens) && this.currentJob.id) {
                  this.modernizationReadinessService.getJobById(this.currentJob.id).subscribe({
                    next: (fullJob) => {
                      this.currentJob = fullJob;
                      this.lastCompletedJobId = fullJob.id;
                      // Refresh job history after a 500ms delay to ensure backend has persisted
                      setTimeout(() => {
                        this.loadJobHistory();
                      }, 500);
                    },
                    error: (err) => {
                      console.error('Error loading completed job details:', err);
                      this.lastCompletedJobId = this.currentJob!.id;
                      // Still refresh history even if we couldn't get full job details
                      setTimeout(() => {
                        this.loadJobHistory();
                      }, 500);
                    }
                  });
                } else {
                  this.lastCompletedJobId = this.currentJob.id;
                }
              }
            } else if (!status.isRunning && !status.isPaused) {   
              // If no job in status but we have a lastCompletedJobId, fetch and display it
              if (!this.currentJob && this.lastCompletedJobId) {
                this.modernizationReadinessService.getJobById(this.lastCompletedJobId).subscribe({
                  next: (job) => {
                    this.currentJob = job;
                  },
                  error: (err) => {
                    console.error('Error loading completed job:', err);
                    // Clear the tracking if we can't load it
                    this.lastCompletedJobId = null;
                  }
                });
              } else if (this.currentJob) {
                const currentStatus = this.currentJob.status;
                
                if (this.isStatusRunning(currentStatus)) {
                  // Only clear running jobs when status reports nothing
                  this.currentJob = null;
                  this.lastCompletedJobId = null;
                }
              }
              // Keep completed/failed/cancelled jobs visible until user dismisses them
            }

            // If API reports a running calculation but did not include full job details,
            // attempt to fetch the job by id (some backends return only an id)
            const reportedJobId = (status as any).currentJobId || (status.currentJob as any)?.id;
            if ((status.isRunning || status.isPaused) && !this.currentJob && reportedJobId) {
              this.modernizationReadinessService.getJobById(Number(reportedJobId)).subscribe({
                next: (job) => {
                  this.currentJob = job;
                },
                error: (err) => {
                  console.error('Error loading current job details:', err);
                }
              });
            }
          },
        error: (err) => {
          console.error('Error loading calculation status:', err);
        }
      });

    // Poll live calculation logs every 3 seconds alongside the status poll
    this.logSubscription = interval(3000)
      .pipe(
        startWith(0),
        switchMap(() => this.modernizationReadinessService.getLiveLogs(this.lastLogId))
      )
      .subscribe({
        next: (response) => {
          if (response.entries?.length) {
            const newLines = response.entries.map(e => this.formatLogEntry(e));
            this.logLines = [...this.logLines, ...newLines];
            this.lastLogId = response.latestId;
            this.scheduleScrollToBottom();
          }
        },
        error: () => { /* live logs are best-effort */ }
      });
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
    this.logSubscription?.unsubscribe();
    if (this.waitingForJobTimer) {
      clearTimeout(this.waitingForJobTimer);
    }
  }

  loadAllData(): void {
    this.loading = true;
    this.error = null;

    forkJoin({
      connections: this.aiSettingsService.getAllConnections().pipe(
        catchError((err) => {
          console.error('Error loading connections:', err);
          this.error = 'Failed to load AI connections';
          return of([] as AiConnection[]);
        })
      ),
      settings: this.modernizationReadinessService.getActiveSettings().pipe(
        catchError((err) => {
          console.error('Error loading active settings:', err);
          if (err.status === 404) {
            this.error = 'No active settings configured. Please create settings first.';
            return of(null);
          }
          if (!this.error) {
            this.error = 'Failed to load active settings';
          }
          return of(null);
        })
      )
    }).subscribe({
      next: ({ connections, settings }) => {
        this.connections = connections;
        this.activeSettings = settings;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });

    this.loadJobHistory();
  }

  // ==================== Connections ====================

  loadConnections(): void {
    this.aiSettingsService.getAllConnections().subscribe({
      next: (connections) => {
        this.connections = connections;
      },
      error: (err) => {
        console.error('Error loading connections:', err);
        this.error = 'Failed to load AI connections';
      }
    });
  }

  // ==================== Settings Management ====================

  loadActiveSettings(): void {
    this.loading = true;
    this.error = null;

    this.modernizationReadinessService.getActiveSettings().subscribe({
      next: (settings) => {
        this.activeSettings = settings;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading active settings:', err);
        if (err.status === 404) {
          this.activeSettings = null;
          this.error = 'No active settings configured. Please create settings first.';
        } else {
          this.error = 'Failed to load active settings';
        }
        this.loading = false;
      }
    });
  }

  loadVersionHistory(): void {
    this.modernizationReadinessService.getAllVersions().subscribe({
      next: (versions) => {
        this.settingsVersions = versions;
        this.showVersionHistory = true;
      },
      error: (err) => {
        console.error('Error loading version history:', err);
        this.error = 'Failed to load version history';
      }
    });
  }

  // Default assessment rules prompt applied when creating new settings
  private readonly defaultAssessmentPrompt = `You are an Enterprise Application Portfolio Assessment Engine.
Your purpose is to evaluate each application independently and determine ONE final outcome:
• Modernize
• Already Modern
• Excluded

OPERATING PRINCIPLES
You operate under a STRICT CLOSED-WORLD ASSUMPTION:
• Use ONLY fields provided in input
• Do NOT infer missing information
• Do NOT use external knowledge
• If a field is blank or null treat it as blank
• Evaluate each application independently
• Apply rules exactly in the order defined
• You MUST be deterministic
• Return ONLY valid JSON matching the schema
• No prose outside JSON output

SECTION 1 — EXCLUSION RULES (HIGHEST PRIORITY)
Mark as Excluded if ANY condition is true.

1A — Lifecycle Status Gate
If LifeCycleStageStatus is blank, null, or anything other than "In Use"
→ Excluded, KeyReason = "Not in use", Recommendation = "Out of Scope", Score = null. STOP.

1B — Managed / Vendor Platforms
If HostingType contains SaaS → Excluded, KeyReason = "SaaS"
If ApplicationManagementStyle contains COTS → Excluded, KeyReason = "COTS"
Recommendation = "Out of Scope", Score = null. STOP.

1C — Sensitive Data
If DataClassification equals Personal Data or Sensitive Personal Data
→ Excluded, KeyReason = "Sensitive data", Recommendation = "Out of Scope", Score = null. STOP.

SECTION 2 — MANDATORY BUSINESS DIRECTIVES (ABSOLUTE PRIORITY)

2A — Retirement Directive (HARD OVERRIDE)
If PlannedDisposition OR LifeCycleStageStatus contains (case-insensitive): retire, retired, retirement, to be retired, decommission, sunset, phase out, end of life, eol
→ Outcome = Excluded, KeyReason = "End of life", Recommendation = "Out of Scope", Score = null. STOP.

SECTION 3 — EVIDENCE SIGNAL EXTRACTION

LegacyArchitecture = TRUE if ArchitectureType contains: client_server, n_tier, platform_host, platform_application
ModernArchitecture = TRUE if ArchitectureType contains: microservice, container, serverless, cloud
ModernPlatform = TRUE if HostingType equals: PaaS, container, serverless, cloud-native
FunctionalOverlap = TRUE if Description contains: duplicate, alternative to, one of several tools, overlapping functionality
LowUsage = TRUE if Description contains: used by small group, limited users, rarely used, maintained for reference
MicroUtility = TRUE if HostingType = Desk OR Description contains: internal utility, team tool, single purpose tool
ReplacementIndicator = TRUE if Description contains: legacy replacement, temporary system, transitional system, replaced by

SECTION 4 — MODERNIZATION SCORE (1–100)
Start baseline = 10
A — HostingType: On Premise/Desk → +35, Hybrid → +25, IaaS → +15, PaaS → +0, Blank/Other → +5
B — Management Style: HomeGrown → +20
C — Architecture Evidence: LegacyArchitecture TRUE AND NOT PaaS → +20; LegacyArchitecture TRUE AND PaaS → +10; ModernArchitecture TRUE → +0; Blank → +5
D — Lifecycle: Maintenance → +5, Deprecated → +10
E — Migration Strategy blank → +5
F — Technical Debt (legacy/outdated/monolithic/technical debt/hard to maintain in Description) → +10
Cap 1–100

SECTION 5 — DECOMMISSION SCORE (1–100)
Start baseline = 5
A — FunctionalOverlap TRUE → +40
B — LowUsage TRUE → +30
C — MicroUtility TRUE → +20
D — ReplacementIndicator TRUE → +20
E — Deprecated → +10, Maintenance → +5
Cap 1–100

SECTION 6 — MODERN PLATFORM PROTECTION
If HostingType = PaaS: Modernize ONLY if ModernizationScore ≥ 70 AND LegacyArchitecture TRUE; otherwise Already Modern.

SECTION 7 — FINAL OUTCOME DETERMINATION
1. Excluded → if Section 1 triggered
2. Excluded (decommission-eligible) → if DecommissionScore > 50 AND DecommissionScore > ModernizationScore
3. Modernize → if ModernizationScore > 50 AND ModernizationScore ≥ DecommissionScore
4. Already Modern → if ModernizationScore ≤ 50 AND DecommissionScore ≤ 50
5. Platform Override → if ModernPlatform TRUE AND ModernizationScore < 70 → Already Modern

SECTION 8 — KEY REASON (2–4 words)
Modernize: Legacy architecture / On-prem hosting / Homegrown system / Technical debt
Already Modern: Cloud-native platform / Modern architecture / Platform managed
Excluded: Not in use / COTS / SaaS / Sensitive data / Functional overlap / Redundant capability / Limited usage / End of life

SECTION 9 — CONFIDENCE SCORING (1–100)
Start baseline = 50
HostingType present → +10, ArchitectureType present → +10, ApplicationManagementStyle present → +10, Description > 15 words → +10, MigrationStrategy present → +5
LegacyArchitecture TRUE → +10, FunctionalOverlap TRUE → +10, ModernPlatform TRUE → +10
Description blank → −15, ArchitectureType blank → −10, HostingType blank → −15
Clamp 1–100

SECTION 10 — RATIONALE (2–3 sentences)
Include major signals, key score drivers, and Confidence Score.

SECTION 11 — RECOMMENDATION
Modernize: LegacyArchitecture TRUE OR HomeGrown → Refactor; Else → Rewrite
Already Modern → No action
Excluded → Out of Scope

SECTION 12 — OUTPUT (STRICT JSON)
[
  {
    "BusinessApplicationID": "",
    "Name": "",
    "Outcome": "",
    "Score": null,
    "KeyReason": "",
    "Rationale": "",
    "Recommendation": ""
  }
]`;

  startCreateSettings(): void {
    this.isCreatingSettings = true;
    this.editingSettings = {
      id: 0,
      version: 1,
      versionName: null,
      isActive: true,
      aiConnectionId: this.connections.length > 0 ? this.connections[0].id : 0,
      prompt: this.defaultAssessmentPrompt,
      deprecatedTechnologies: '',
      apiDelayMs: 200,
      batchSize: 50,
      maxRetriesPerApplication: 3,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  editSettings(settings: AiModernizationReadinessSettingsDetail): void {
    this.isCreatingSettings = false;
    this.editingSettings = {
      id: settings.id,
      version: settings.version,
      versionName: settings.versionName,
      isActive: settings.isActive,
      aiConnectionId: settings.connection.id,
      prompt: settings.prompt,
      deprecatedTechnologies: settings.deprecatedTechnologies,
      apiDelayMs: settings.apiDelayMs,
      batchSize: settings.batchSize,
      maxRetriesPerApplication: settings.maxRetriesPerApplication,
      createdAt: settings.createdAt,
      updatedAt: new Date()
    };
  }

  cancelEditSettings(): void {
    this.editingSettings = null;
    this.isCreatingSettings = false;
  }

  saveSettings(): void {
    if (!this.editingSettings) {
      return;
    }

    // Validation
    if (!this.editingSettings.aiConnectionId) {
      this.error = 'Please select an AI connection';
      return;
    }
    if (!this.editingSettings.prompt?.trim()) {
      this.error = 'Prompt is required';
      return;
    }

    this.saving = true;
    this.error = null;
    this.successMessage = null;

    // Settings are immutable versions - always create a new version using the simplified endpoint
    this.modernizationReadinessService.createSimplifiedSettings(
      this.editingSettings.aiConnectionId,
      this.editingSettings.prompt,
      this.editingSettings.deprecatedTechnologies
    ).subscribe({
      next: (savedSettings) => {
        this.successMessage = 'Settings saved successfully!';
        this.saving = false;
        this.editingSettings = null;
        this.isCreatingSettings = false;
        this.activeSettings = savedSettings;
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.successMessage = null;
        }, 3000);
      },
      error: (err) => {
        console.error('Error saving settings:', err);
        this.error = err.error?.message || 'Failed to save settings';
        this.saving = false;
      }
    });
  }

  activateVersion(versionId: number): void {
    if (!confirm('Are you sure you want to activate this version? It will become the active configuration.')) {
      return;
    }

    this.modernizationReadinessService.activateVersion(versionId).subscribe({
      next: () => {
        this.successMessage = 'Version activated successfully!';
        this.loadActiveSettings();
        this.loadVersionHistory();
        
        setTimeout(() => {
          this.successMessage = null;
        }, 3000);
      },
      error: (err) => {
        console.error('Error activating version:', err);
        this.error = err.error?.message || 'Failed to activate version';
      }
    });
  }

  deleteVersion(versionId: number): void {
    if (!confirm('Are you sure you want to delete this version? This action cannot be undone.')) {
      return;
    }

    this.modernizationReadinessService.deleteVersion(versionId).subscribe({
      next: () => {
        this.successMessage = 'Version deleted successfully!';
        this.loadVersionHistory();
        
        setTimeout(() => {
          this.successMessage = null;
        }, 3000);
      },
      error: (err) => {
        console.error('Error deleting version:', err);
        this.error = err.error?.message || 'Failed to delete version';
      }
    });
  }

  // ==================== Calculation Trigger ====================

  triggerCalculation(): void {
    if (this.calculationStatus?.isRunning) {
      this.error = 'A calculation is already running';
      return;
    }

    if (!this.activeSettings) {
      this.error = 'No active settings configured. Please create and activate settings first.';
      return;
    }

    if (!confirm('This will calculate modernization readiness scores for ALL applications in your CMDB. This may take several minutes to hours depending on the number of applications. Continue?')) {
      return;
    }

    this.triggering = true;
    this.error = null;
    this.successMessage = null;
    this.lastCompletedJobId = null; // Clear any previous completed job

    this.modernizationReadinessService.triggerCalculation().subscribe({
      next: (job) => {
        this.successMessage = 'Calculation started successfully!' + (job.id ? ' Job ID: ' + job.id : '');
        this.triggering = false;
        this.currentJob = job;
        // Clear log console so only this run's logs are displayed
        this.logLines = [];
        this.lastLogId = 0;
        this.selectedTabIndex = 1; // Switch to Calculate tab
        
        // Set waiting flag to show spinner until polling picks up the job
        this.waitingForJob = true;
        // Clear waiting flag after 10 seconds as a failsafe
        this.waitingForJobTimer = setTimeout(() => {
          this.waitingForJob = false;
        }, 10000);
        
        // Immediately update calculation status based on actual job state
        if (this.calculationStatus) {
          this.calculationStatus.isRunning = this.isStatusRunning(job.status);
          this.calculationStatus.isPaused = false;
          this.calculationStatus.currentJob = job;
        } else {
          this.calculationStatus = {
            isRunning: this.isStatusRunning(job.status),
            isPaused: false,
            currentJob: job,
            queueLength: 0,
            totalPromptTokens: 0,
            totalCompletionTokens: 0,
            totalTokens: 0
          };
        }
        
        // Refresh job list immediately
        this.loadJobHistory();
        
        setTimeout(() => {
          this.successMessage = null;
        }, 5000);
      },
      error: (err) => {
        console.error('Error triggering calculation:', err);
        if (err.status === 409) {
          this.error = 'A calculation is already in progress';
        } else {
          this.error = err.error?.message || 'Failed to trigger calculation';
        }
        this.triggering = false;
      }
    });
  }

  triggerCalculationForOne(): void {
    if (this.calculationStatus?.isRunning) {
      this.error = 'A calculation is already running';
      return;
    }

    if (!this.activeSettings) {
      this.error = 'No active settings configured. Please create and activate settings first.';
      return;
    }

    if (!this.singleAppQuery || !this.singleAppQuery.trim()) {
      this.error = 'Please enter an application SysId or name to calculate for.';
      return;
    }

    if (!confirm('This will calculate modernization readiness scores for the specified application. Continue?')) {
      return;
    }

    this.singleAppTriggering = true;
    this.error = null;
    this.successMessage = null;
    this.lastCompletedJobId = null; // Clear any previous completed job

    this.modernizationReadinessService.triggerCalculationForOne(this.singleAppQuery.trim()).subscribe({
      next: (response) => {
        this.successMessage = 'Calculation started successfully!' + (response.jobId ? ' Job ID: ' + response.jobId : '');
        
        // Fetch the full job details by ID before clearing the triggering flag
        if (response.jobId) {
          this.modernizationReadinessService.getJobById(response.jobId).subscribe({
            next: (job) => {
              this.currentJob = job;
              // Clear log console so only this run's logs are displayed
              this.logLines = [];
              this.lastLogId = 0;
              this.singleAppTriggering = false; // Clear flag after job is loaded
              this.selectedTabIndex = 1; // Switch to Calculate tab
              
              // Set waiting flag to show spinner until polling picks up the job
              this.waitingForJob = true;
              // Clear waiting flag after 10 seconds as a failsafe
              this.waitingForJobTimer = setTimeout(() => {
                this.waitingForJob = false;
              }, 10000);
              
              // Update calculation status based on actual job state
              if (this.calculationStatus) {
                this.calculationStatus.isRunning = this.isStatusRunning(job.status);
                this.calculationStatus.isPaused = false;
                this.calculationStatus.currentJob = job;
              } else {
                this.calculationStatus = {
                  isRunning: this.isStatusRunning(job.status),
                  isPaused: false,
                  currentJob: job,
                  queueLength: 0,
                  totalPromptTokens: 0,
                  totalCompletionTokens: 0,
                  totalTokens: 0
                };
              }

              // For single app calculations that complete quickly, poll for completion and then refresh job history
              const pollForCompletion = () => {
                if (!this.currentJob || !this.currentJob.id) {
                  return;
                }
                
                this.modernizationReadinessService.getJobById(this.currentJob.id).subscribe({
                  next: (updatedJob) => {
                    this.currentJob = updatedJob;
                    
                    // Check if job completed
                    if (this.isStatusCompleted(updatedJob.status) || 
                        this.isStatusFailed(updatedJob.status) || 
                        this.isStatusCancelled(updatedJob.status)) {
                      // Job completed, wait a bit for token data to persist then refresh history
                      setTimeout(() => {
                        this.loadJobHistory();
                      }, 500);
                    } else if (this.isStatusRunning(updatedJob.status)) {
                      // Still running, poll again
                      setTimeout(pollForCompletion, 1000);
                    }
                  },
                  error: (err) => {
                    console.error('Error polling for job completion:', err);
                  }
                });
              };
              
              // Start polling after a brief delay
              setTimeout(pollForCompletion, 1000);
            },
            error: (err) => {
              console.error('Error fetching job details:', err);
              this.singleAppTriggering = false; // Clear flag even on error
            }
          });
        } else {
          // No job ID returned, clear the flag
          this.singleAppTriggering = false;
        }

        setTimeout(() => {
          this.successMessage = null;
        }, 5000);
      },
      error: (err) => {
        console.error('Error triggering single-app calculation:', err);
        this.error = err.error?.message || 'Failed to trigger calculation for the specified application';
        this.singleAppTriggering = false;
      }
    });
  }

  pauseCalculation(): void {
    this.pausing = true;
    this.error = null;

    this.modernizationReadinessService.pauseCalculation().subscribe({
      next: () => {
        this.successMessage = 'Calculation paused';
        this.pausing = false;
        
        // Force immediate status refresh
        this.modernizationReadinessService.getCalculationStatus().subscribe({
          next: (status) => {
            this.calculationStatus = status;
            this.currentJob = status.currentJob || null;
          }
        });
        
        setTimeout(() => { this.successMessage = null; }, 3000);
      },
      error: (err) => {
        console.error('Error pausing calculation:', err);
        this.error = err.error?.message || 'Failed to pause calculation';
        this.pausing = false;
      }
    });
  }

  resumeCalculation(): void {
    this.resuming = true;
    this.error = null;

    this.modernizationReadinessService.resumeCalculation().subscribe({
      next: () => {
        this.successMessage = 'Calculation resumed';
        this.resuming = false;
        
        // Force immediate status refresh
        this.modernizationReadinessService.getCalculationStatus().subscribe({
          next: (status) => {
            this.calculationStatus = status;
            this.currentJob = status.currentJob || null;
          }
        });
        
        setTimeout(() => { this.successMessage = null; }, 3000);
      },
      error: (err) => {
        console.error('Error resuming calculation:', err);
        this.error = err.error?.message || 'Failed to resume calculation';
        this.resuming = false;
      }
    });
  }

  cancelCalculation(): void {
    if (!confirm('Are you sure you want to cancel the running calculation? This cannot be undone.')) {
      return;
    }

    this.cancelling = true;
    this.error = null;

    this.modernizationReadinessService.cancelCalculation().subscribe({
      next: () => {
        this.successMessage = 'Calculation cancelled';
        this.cancelling = false;
        
        // Force immediate status refresh
        this.modernizationReadinessService.getCalculationStatus().subscribe({
          next: (status) => {
            this.calculationStatus = status;
            this.currentJob = status.currentJob || null;
          }
        });
        
        setTimeout(() => { this.successMessage = null; }, 3000);
      },
      error: (err) => {
        console.error('Error cancelling calculation:', err);
        this.error = err.error?.message || 'Failed to cancel calculation';
        this.cancelling = false;
      }
    });
  }

  // ==================== Job History ====================

  loadJobHistory(): void {
    this.jobHistoryLoading = true;
    this.modernizationReadinessService.getJobHistory(this.jobHistoryPage, this.jobHistoryPageSize).subscribe({
      next: (response) => {
        const jobs: ModernizationReadinessJob[] = response?.jobs ?? response ?? [];
        const totalCount: number = response?.totalCount ?? jobs.length;
        const totalPages: number = response?.totalPages ?? (Math.ceil(totalCount / this.jobHistoryPageSize) || 1);
        this.jobHistory = jobs;
        this.jobHistoryTotalCount = totalCount;
        this.jobHistoryTotalPages = totalPages;
        this.jobHistoryLoading = false;
      },
      error: (err) => {
        console.error('Error loading job history:', err);
        this.error = 'Failed to load job history';
        this.jobHistoryLoading = false;
      }
    });
  }

  onJobHistoryPageChange(event: PageEvent): void {
    this.jobHistoryPage = event.pageIndex + 1;
    this.jobHistoryPageSize = event.pageSize;
    this.loadJobHistory();
  }

  viewJobDetails(job: ModernizationReadinessJob): void {
    this.router.navigate(['/admin/modernization-readiness/job', job.id]);
  }

  // ==================== Helper Methods ====================

  isStatusRunning(status: ModernizationReadinessJobStatus | string | number | undefined): boolean {
    if (status === undefined || status === null) return false;
    return status === 'Running' || 
           status === ModernizationReadinessJobStatus.Running || 
           status === 1 || // Running enum value
           (typeof status === 'string' && status.toLowerCase() === 'running');
  }

  isStatusCompleted(status: ModernizationReadinessJobStatus | string | number | undefined): boolean {
    if (status === undefined || status === null) return false;
    return status === 'Completed' || 
           status === ModernizationReadinessJobStatus.Completed ||
           status === 2 || // Completed enum value
           (typeof status === 'string' && status.toLowerCase() === 'completed');
  }

  isStatusFailed(status: ModernizationReadinessJobStatus | string | number | undefined): boolean {
    if (status === undefined || status === null) return false;
    return status === 'Failed' || 
           status === ModernizationReadinessJobStatus.Failed ||
           status === 3 || // Failed enum value
           (typeof status === 'string' && status.toLowerCase() === 'failed');
  }

  isStatusCancelled(status: ModernizationReadinessJobStatus | string | number | undefined): boolean {
    if (status === undefined || status === null) return false;
    return status === 'Cancelled' || 
           status === ModernizationReadinessJobStatus.Cancelled ||
           status === 4 || // Cancelled enum value
           (typeof status === 'string' && status.toLowerCase() === 'cancelled');
  }

  isStatusPending(status: ModernizationReadinessJobStatus | string | number | undefined): boolean {
    if (status === undefined || status === null) return false;
    return status === 'Pending' || 
           status === ModernizationReadinessJobStatus.Pending ||
           status === 0 || // Pending enum value
           (typeof status === 'string' && status.toLowerCase() === 'pending');
  }

  getProgressPercentage(job: ModernizationReadinessJob | null): number {
    if (!job || job.totalApplications === 0) {
      return 0;
    }
    return Math.round((job.processedCount / job.totalApplications) * 100);
  }

  formatDuration(job: ModernizationReadinessJob): string {
    if (!job.startedAt) {
      return 'Not started';
    }

    const start = new Date(job.startedAt).getTime();
    const end = job.completedAt ? new Date(job.completedAt).getTime() : Date.now();

    const durationMs = end - start;
    const totalMinutes = Math.floor(durationMs / 60000);
    let hours = Math.floor(totalMinutes / 60) - 1; // Subtract 1 hour to account for timezone offset in display
    hours = hours < 0 ? 0 : hours; // Ensure we don't show negative hours
    const remainingMinutes = totalMinutes % 60;

    return `${hours}h ${remainingMinutes}m`;
  }

  formatDate(date: Date | null | undefined): string {
    if (!date) {
      return 'N/A';
    }
    return formatUtcDate(new Date(date), 'short');
  }

  getStatusBadgeClass(status: ModernizationReadinessJobStatus | string): string {
    const statusStr = typeof status === 'string' ? status : ModernizationReadinessJobStatus[status];
    switch (statusStr) {
      case 'Completed':
        return 'badge-success';
      case 'Running':
        return 'badge-primary';
      case 'Failed':
        return 'badge-danger';
      case 'Cancelled':
        return 'badge-warning';
      case 'Pending':
        return 'badge-secondary';
      case 'Paused':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  }

  getStatusText(status: ModernizationReadinessJobStatus | string): string {
    const statusStr = typeof status === 'string' ? status : ModernizationReadinessJobStatus[status];
    return statusStr || 'Unknown';
  }

  getRecommendationBadgeClass(recommendation: string | null): string {
    switch (recommendation?.toLowerCase()) {
      case 'retire':
        return 'badge-danger';
      case 'rehost':
        return 'badge-warning';
      case 'replatform':
        return 'badge-info';
      case 'refactor':
        return 'badge-primary';
      case 'retain':
        return 'badge-success';
      default:
        return 'badge-secondary';
    }
  }

  getScoreBadgeClass(score: number | null): string {
    if (score === null || score === undefined) {
      return 'badge-secondary';
    }
    
    if (score <= 30) {
      return 'badge-danger';
    } else if (score <= 60) {
      return 'badge-warning';
    } else if (score <= 85) {
      return 'badge-info';
    } else {
      return 'badge-success';
    }
  }

  formatTokenCount(tokens: number): string {
    if (!tokens || tokens === 0) {
      return '0';
    }
    if (tokens >= 1000000) {
      return (tokens / 1000000).toFixed(1) + 'M';
    }
    if (tokens >= 1000) {
      return (tokens / 1000).toFixed(1) + 'K';
    }
    return tokens.toString();
  }

  dismissCompletedJob(): void {
    this.currentJob = null;
    this.lastCompletedJobId = null;
    this.successMessage = null;
    this.error = null;
  }

  startNewCalculation(): void {
    this.dismissCompletedJob();
    // Stay on current tab for user to trigger new calc
  }

  // ==================== Live Log Console ====================

  clearLogs(): void {
    this.logLines = [];
    // Do NOT reset lastLogId — keeps the cursor position so next poll
    // only fetches new entries and does not re-fill the cleared console.
  }

  private formatLogEntry(entry: LiveLogEntry): string {
    const dt = new Date(entry.timestamp);
    const h = this.pad2(dt.getHours());
    const m = this.pad2(dt.getMinutes());
    const s = this.pad2(dt.getSeconds());
    const ms = String(dt.getMilliseconds()).padStart(3, '0');
    const time = `${h}:${m}:${s}.${ms}`;
    const level = entry.level.substring(0, 4).toUpperCase().padEnd(4);
    const shortCat = entry.category.split('.').pop() ?? entry.category;
    return `${time} [${level}] ${shortCat}: ${entry.message}`;
  }

  private pad2(n: number): string {
    return n.toString().padStart(2, '0');
  }

  private scheduleScrollToBottom(): void {
    setTimeout(() => {
      if (this.logConsoleEl) {
        const el = this.logConsoleEl.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 40);
  }

  onTabChange(event: any): void {
    // When switching to Job History tab (index 2), refresh the list
    if (event.index === 2) {
      this.loadJobHistory();
    }
    // When switching to Calculate tab (index 1), check for completed job
    else if (event.index === 1) {
      // If we have a lastCompletedJobId but no currentJob, fetch it
      if (this.lastCompletedJobId && !this.currentJob) {
        this.modernizationReadinessService.getJobById(this.lastCompletedJobId).subscribe({
          next: (job) => {
            this.currentJob = job;
          },
          error: (err) => {
            console.error('Error loading completed job:', err);
          }
        });
      }
    }
  }
}
